/**
 * The three sizes, from BRIEF.md:
 *
 *   under 700px   phone   — one card per worker, navigation in a bottom bar
 *   700–1180px    tablet  — tabs across the top, the ten-column table
 *   above 1180px  desktop — tabs as a left rail, plus the running totals panel
 *
 * The tablet and desktop difference is pure CSS, so only the phone break needs
 * to reach JavaScript: below it the timesheet is a different component, not a
 * restyled table. Anything that is only a matter of layout stays in the
 * stylesheet.
 */

import { useEffect, useState } from 'react';

export const PHONE_MAX = 699;

function subscribe(query: MediaQueryList, listener: () => void): () => void {
  query.addEventListener('change', listener);
  return () => query.removeEventListener('change', listener);
}

export function useMediaQuery(query: string): boolean {
  const [matches, setMatches] = useState(() =>
    typeof window === 'undefined' ? false : window.matchMedia(query).matches,
  );

  useEffect(() => {
    const list = window.matchMedia(query);
    const update = () => setMatches(list.matches);
    update();
    return subscribe(list, update);
  }, [query]);

  return matches;
}

/** True on a phone — the one breakpoint that changes what is rendered. */
export function useIsPhone(): boolean {
  return useMediaQuery(`(max-width: ${PHONE_MAX}px)`);
}
